/* CaseHeader — iter101.99 (admin variant)
 *
 * Rewrite of the admin case-detail header to compose the shared
 * `/components/shared/CommandHeaderShell`, matching the visual
 * grammar of the doctor portal's `DoctorCaseCommandHeader`. Both
 * roles now see the same editorial identity band (avatar + name
 * + demographics + assignments) and the same 6-chip cockpit strip
 * (Case ID · Created · Planning · Manufacturing · IO Scans ·
 * Compliance).
 *
 * Admin-specific bits that survive the redesign:
 *   - "Back to Cases" link above the shell
 *   - Assignments show Clinic + Doctor (doctor version shows Clinic
 *     + Patient phone — admins prioritise knowing WHO the doctor is)
 *   - Right slot carries admin actions: Send-to-Production CTA
 *     (visible only for the routing-decision states), PDF download,
 *     and Force Status overflow menu
 *
 * Retired: Balance chip (surfaced in PaymentsCard), Tier chip (rarely
 * referenced), Approved-at inline line (available on the timeline),
 * ID copy button (data-strip already shows the Case ID in mono).
 *
 * Preserved testids: `case-header-v2`, `case-back-link`,
 * `case-patient-profile-link`, `case-status-pill`, `case-header-actions`,
 * `send-to-production-btn`, `case-pdf-btn`, `case-overflow-btn`,
 * `case-force-status-menu-item`.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, DownloadSimple, DotsThreeVertical, ArrowRight,
    Buildings, Crown, Hash, CalendarBlank, PencilSimple, Sparkle,
    Timer, ScanSmiley, ShieldCheck, Stethoscope,
} from "@phosphor-icons/react";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";
import PatientAvatar from "@/components/patient/PatientAvatar";
import { fmtDate } from "./format";


export function CaseHeader({ c, blockers, onForceStatus, onSendToProduction }) {
    const [menuOpen, setMenuOpen] = useState(false);

    const status = c?.status;

    /* ── Send-to-Production visibility ────────────────────────────────
     * Show for every state where admin's action is required to send
     * the case to production; disable visually when there are hard
     * blockers so admin still gets the affordance but can't fire. */
    const hardBlockers = (blockers || []).filter((b) => b.severity === "blocker");
    const NEEDS_ROUTING_STATUSES = new Set([
        "approved", "awaiting_production_assignment", "submitted_to_production",
    ]);
    const needsRouting = NEEDS_ROUTING_STATUSES.has(status) && !c?.production_tech_id;
    const canSendToProduction = needsRouting && hardBlockers.length === 0;
    const showSendCta = needsRouting;

    /* ── Identity / avatar payload ────────────────────────────────── */
    const patient = {
        id: c?.patient_id,
        name: c?.patient_name,
        photo_data_url: c?.patient_photo_data_url,
    };

    /* ── Demographics — admin only reliably has the patient code / id.
     * The doctor's header adds age/gender because it fetches a separate
     * `patient` prop with those fields; admin case DTO doesn't. */
    const patientIdStr = c?.patient_code
        || (c?.patient_id ? `INV-PT-${String(c.patient_id).replace(/-/g, "").slice(0, 6).toUpperCase()}` : null);
    const demographics = patientIdStr ? [patientIdStr] : [];
    const demographicsSuffix = c?.patient_id ? (
        <Link
            to={`/admin/patients/${c.patient_id}`}
            className="text-primary hover:underline font-semibold"
            data-testid="case-patient-profile-link"
        >
            Open profile
        </Link>
    ) : null;

    /* ── Assignment mini-stack — Clinic + Doctor ───────────────────── */
    const clinicName = c?.practice_location_name || c?.doctor_clinic_name;
    const assignments = [
        {
            label: "Clinic",
            value: clinicName || "—",
            icon: Buildings,
            testid: "case-header-clinic-name",
            title: clinicName,
        },
        ...(c?.doctor_name ? [{
            label: "Doctor",
            value: c.doctor_name,
            icon: Crown,
            testid: "case-header-doctor-name",
            title: c.doctor_name,
        }] : []),
    ];

    /* ── Data-strip chip values (mirrors DoctorCaseCommandHeader) ── */
    const pricing = c?.pricing || {};
    const priorityCode = pricing.priority_code || "standard";
    const priorityLabel = pricing.priority_label || "Standard";
    const priorityTat = pricing.priority_tat_days;
    const mfgValue = priorityCode === "standard"
        ? `Standard${priorityTat ? ` · ${priorityTat}d TAT` : ""}`
        : `${priorityLabel}${priorityTat ? ` · ${priorityTat}d TAT` : ""}`;

    const ioPay = pricing.io_scanning_payment_choice;
    const ioFee = pricing.scanner_pickup_fee;
    const hasIoScan = Boolean(ioPay)
        || (typeof ioFee === "number" && ioFee > 0)
        || Boolean(pricing.scanner_pickup_requested);
    const ioValue = hasIoScan ? "Requested" : "Not requested";

    const complianceUpgraded = c?.coe_support_compliance === true;
    const complianceValue = complianceUpgraded ? "Invisique™ manages" : "Self-managed";
    const planningValue = c?.planning_mode === "doctor" ? "Self-planned" : "Invisique™ Team";

    /* ── Right slot (admin actions) ────────────────────────────────── */
    const rightSlot = (
        <div
            className="flex items-center justify-end gap-1.5 flex-wrap"
            data-testid="case-header-actions"
        >
            {showSendCta && (
                <button
                    onClick={onSendToProduction}
                    disabled={!canSendToProduction}
                    title={!canSendToProduction ? `Resolve ${hardBlockers.length} blocker${hardBlockers.length > 1 ? "s" : ""} first` : "Send case to production"}
                    className={`inline-flex items-center gap-1 px-3 h-7 rounded-full text-[11px] font-semibold transition ${canSendToProduction
                        ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                        : "bg-emerald-600/30 text-white/80 cursor-not-allowed"}`}
                    data-testid="send-to-production-btn"
                >
                    Send to Production <ArrowRight size={11} weight="bold" />
                </button>
            )}
            <a
                href={`/api/cases/${c?.id}/pdf/submission`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11px] font-semibold text-muted-foreground border border-border hover:text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
                data-testid="case-pdf-btn"
                title="Download case submission PDF"
            >
                <DownloadSimple size={11} weight="bold" /> PDF
            </a>
            <div className="relative">
                <button
                    onClick={() => setMenuOpen((v) => !v)}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/5 transition-colors"
                    data-testid="case-overflow-btn"
                    title="More actions"
                >
                    <DotsThreeVertical size={13} weight="bold" />
                </button>
                {menuOpen && (
                    <>
                        <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                        <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-lg border border-border bg-card shadow-lg py-1 text-sm">
                            <button
                                onClick={() => { setMenuOpen(false); onForceStatus?.(); }}
                                className="w-full text-left px-3 py-1.5 hover:bg-secondary text-amber-700"
                                data-testid="case-force-status-menu-item"
                            >
                                Force Status…
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div data-testid="case-header-v2">
            <Link
                to="/admin/cases"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
                data-testid="case-back-link"
            >
                <ArrowLeft size={12} /> Back to Cases
            </Link>

            <CommandHeaderShell
                testId="admin-case-command-header"
                avatar={<PatientAvatar patient={patient} size="xl" className="ring-2 ring-primary/15" />}
                identityLabel="Patient · Case"
                name={c?.patient_name || "Unnamed patient"}
                nameTitle={c?.patient_name}
                demographics={demographics}
                demographicsSuffix={demographicsSuffix}
                assignments={assignments}
                rightSlot={rightSlot}
                statusPill={(
                    <span data-testid="case-status-pill">
                        <CaseStatusBadge status={status} size="md" portal="admin" />
                    </span>
                )}
                chips={[
                    { icon: Hash, label: "Case ID", value: c?.code || "—", mono: true, testid: "case-chip-code", title: c?.code },
                    { icon: CalendarBlank, label: "Created", value: fmtDate(c?.created_at), testid: "case-chip-created" },
                    {
                        icon: c?.planning_mode === "doctor" ? PencilSimple : Sparkle,
                        label: "Planning",
                        value: planningValue,
                        testid: c?.planning_mode === "doctor" ? "case-planning-mode-self" : "case-planning-mode-coe",
                        title: c?.planning_mode === "doctor"
                            ? "The doctor authored the treatment plan on this case"
                            : "Invisique™ Planning Team authored the treatment plan",
                    },
                    {
                        icon: Timer,
                        label: "Manufacturing",
                        value: mfgValue,
                        testid: "case-chip-manufacturing",
                        title: priorityTat ? `Expected manufacturing turnaround: ${priorityTat} days` : "Manufacturing turnaround by tier",
                    },
                    {
                        icon: ScanSmiley,
                        label: "IO Scans",
                        value: ioValue,
                        testid: "case-chip-io-scan",
                        title: hasIoScan
                            ? "Invisique will send an IO-scanning partner to the clinic"
                            : "Doctor submits their own PVS impressions or intraoral scans",
                    },
                    {
                        icon: complianceUpgraded ? ShieldCheck : Stethoscope,
                        label: "Compliance",
                        value: complianceValue,
                        tone: complianceUpgraded ? "emerald" : "rose",
                        testid: complianceUpgraded ? "case-monitoring-mode-invisique" : "case-monitoring-mode-empty-readonly",
                        title: complianceUpgraded
                            ? "Invisique™ runs patient-side compliance & monitoring on this case"
                            : "Doctor is self-managing compliance on this case",
                    },
                ]}
            />
        </div>
    );
}
